import { z } from 'zod';

import { MESSAGE_SENDER_TYPES } from '../conversation.js';
import { SIDE_EFFECTS } from '../risk.js';

/**
 * Chat, as the SPA sees it - API-008.
 *
 * **What the wire calls a conversation is a `thread` row.** The `conversation`
 * table is the channel a thread belongs to, and for `WEB` there is one of those
 * per thread: the channel and the topic coincide when the channel is "the web
 * app". They are separate tables because a Slack channel holds many threads, and
 * collapsing them now would mean a migration then. The SPA has no use for that
 * distinction, so the wire does not carry it.
 */

/** The database caps neither. An unbounded string from a form is a gap. */
export const CONVERSATION_TITLE_MAX = 120;
export const MESSAGE_TEXT_MAX = 8000;

/**
 * A message body, and the two kinds there are today.
 *
 * `TEXT` is what a person writes and what an employee answers with.
 *
 * `NOT_CONNECTED` **is history and is kept for the rows that already carry it.**
 * It was the placeholder an employee answered with before MODEL-002 connected a
 * model; nothing writes one now. It stays in the union because messages are
 * append-only by design, so those rows are still in their threads and still
 * have to decode - and it is a kind rather than prose so the SPA renders one as
 * a notice and never as the employee talking. Removing it would make a thread
 * from that period fail to parse.
 *
 * `APPROVAL` is a request the employee is making of the person: it renders as a
 * card with two buttons rather than as the employee talking, and the decision
 * arrives as a *new* message because messages are append-only.
 *
 * The union widens again when a reply can carry a citation.
 */
export const MESSAGE_CONTENT_KINDS = ['TEXT', 'NOT_CONNECTED', 'APPROVAL'] as const;
export type MessageContentKind = (typeof MESSAGE_CONTENT_KINDS)[number];

/**
 * **A discriminated union now, and it was a flat object.**
 *
 * `{ kind, text }` was right while both kinds carried nothing but text.
 * `APPROVAL` carries what §14.3 says a request must show - the app, the action,
 * the target, who asked - and there is no honest way to put that in a `text`
 * field. A flat object with optional extras would also have let a `TEXT`
 * message be written with an approval id, which is a state nothing should be
 * able to express.
 *
 * `z.discriminatedUnion` and not `z.union`: it fails on the *arm*, so a
 * malformed approval says which field is wrong instead of "no variant matched".
 */
export const messageContentSchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('TEXT'), text: z.string() }).readonly(),
    z.object({ kind: z.literal('NOT_CONNECTED'), text: z.string() }).readonly(),
    z
      .object({
        kind: z.literal('APPROVAL'),
        /** The row in `approval`. What the buttons act on. */
        approvalId: z.string().uuid(),
        /** One sentence naming the app, the action and the target - §14.3. */
        text: z.string(),
        /** So the screen can say how dangerous it is without a second read. */
        sideEffect: z.enum(SIDE_EFFECTS),
        expiresAt: z.string(),
      })
      .readonly(),
  ])
  .readonly();

export const chatMessageSchema = z
  .object({
    id: z.uuid(),
    senderType: z.enum(MESSAGE_SENDER_TYPES),
    /** Present exactly when `senderType` is `USER` - the schema holds the two consistent. */
    senderUserId: z.uuid().optional(),
    content: messageContentSchema,
    createdAt: z.string(),
  })
  .readonly();

export const conversationSummarySchema = z
  .object({
    /** The thread id. See the note at the top of this file. */
    id: z.uuid(),
    title: z.string(),
    /** The employee answering - EMP-008. Absent only for a thread made before one existed. */
    employeeId: z.uuid().optional(),
    createdAt: z.string(),
  })
  .readonly();

export const createConversationRequestSchema = z
  .object({
    title: z.string().trim().max(CONVERSATION_TITLE_MAX).optional(),
    /** Defaults to the workspace's Generalist, which is what EMP-009 asks for. */
    employeeId: z.uuid().optional(),
  })
  .readonly();

export const sendMessageRequestSchema = z
  .object({ text: z.string().trim().min(1).max(MESSAGE_TEXT_MAX) })
  .readonly();

export const conversationListResponseSchema = z
  .object({ conversations: z.array(conversationSummarySchema).readonly() })
  .readonly();

export const conversationResponseSchema = z
  .object({ conversation: conversationSummarySchema })
  .readonly();

export const messageListResponseSchema = z
  .object({ messages: z.array(chatMessageSchema).readonly() })
  .readonly();

/**
 * What the send wrote, and where the answer will appear.
 *
 * **One row now, not two.** Until MODEL-002 this route wrote the question and a
 * `NOT_CONNECTED` placeholder in the same transaction, and returned both -
 * because both existed the moment the request finished. A real answer does not:
 * it arrives when the run that produces it finishes, which is why `runId` is
 * here. The client polls `GET /v1/runs/{id}` and refetches the thread; that is
 * also the shape a streaming transport would replace without changing this
 * response.
 *
 * `messages` stays an array rather than becoming a single object, so a future
 * send that writes more than one row does not change the shape again.
 */
export const sendMessageResponseSchema = z
  .object({
    messages: z.array(chatMessageSchema).readonly(),
    /** The run that will answer. Poll it; the answer lands in the thread. */
    runId: z.uuid(),
  })
  .readonly();

export type MessageContent = z.infer<typeof messageContentSchema>;
export type ChatMessage = z.infer<typeof chatMessageSchema>;
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;
export type CreateConversationRequest = z.infer<typeof createConversationRequestSchema>;
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;
export type ConversationResponse = z.infer<typeof conversationResponseSchema>;
export type MessageListResponse = z.infer<typeof messageListResponseSchema>;
export type SendMessageResponse = z.infer<typeof sendMessageResponseSchema>;
